import { create } from 'zustand';
import apiClient, {
  isTokenExpired as checkTokenExpired,
} from '../service/apiClient';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';
import { ResponseError } from '../type/type';

type LoginData = {
  isAuthenticated: boolean;
  isTokenExpired: boolean;
  name: string;
  role: 'ROLE_USER' | 'ROLE_ADMIN' | 'ROLE_CS' | 'ROLE_TRACKING' | 'ROLE_MARKETING';
};

type LoginAction = {
  login: (loginId: string, loginPw: string) => Promise<{ success: boolean; message: string }>;
  register: (
    loginId: string,
    loginPw: string,
    name: string,
    telNo: string,
    businessNumber: String,
    email: string,
    bizProfileName: String,
    companyName: string,
    companyTelNo: string,
    address: string,
    ceoName: string
  ) => Promise<{ success: boolean; message: string }>;
  autoLogin: () => void;
  logout: () => void;
  tokenExpire: () => void;
};

type LoginResponse = {
  loginId: string;
  name: string;
  telNo: string;
  role: 'ROLE_USER' | 'ROLE_ADMIN' | 'ROLE_CS' | 'ROLE_TRACKING' | 'ROLE_MARKETING';
  franchise: string;
  accessToken: string;
};

const initialState: LoginData = {
  isAuthenticated: false,
  isTokenExpired: false,
  name: '',
  role: 'ROLE_USER',
};

export const useLoginStore = create<LoginData & LoginAction>(set => ({
  ...initialState,
  login: async (loginId, loginPw) => {
    try {
      const { data } = await apiClient.post<LoginResponse>('/login', {
        loginId,
        loginPw,
      });
      localStorage.setItem('token', data.accessToken);
      set({ isAuthenticated: true, name: data.name, role: data.role });
      return { success: true, message: '로그인 성공' };
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('아이디 또는 패스워드가 틀렸습니다.');
      return { success: false, message: '아이디 또는 패스워드가 틀렸습니다.' };
    }
  },
  register: async (loginId, loginPw, name, telNo, businessNumber, email, bizProfileName, companyName, companyTelNo, address, ceoName) => {
    try {
      await apiClient.post('/customer/join-franchise', {
        loginId,
        loginPw,
        name,
        telNo,
        businessNumber,
        email,
        bizProfileName,
        companyName,
        companyTelNo,
        address,
        ceoName,
      });
      toast.success('회원가입에 성공했습니다.');

      const { data } = await apiClient.post<LoginResponse>('/login', {
        loginId,
        loginPw,
      });
      localStorage.setItem('token', data.accessToken);
      set({ isAuthenticated: true, name: data.name, role: data.role });
      return { success: true, message: '회원가입에 성공했습니다.' };
    } catch (error) {
      let errorMessage = '회원가입에 실패했습니다.';
      if (isAxiosError<ResponseError>(error)) {
        if(error.response?.data.errorData) {
          errorMessage = error.response?.data.errorData;
        } else if(error.response?.data.errorMsg) {
          errorMessage = error.response?.data.errorMsg;
        }
      } else {
        console.error('API 요청 실패:', error);
      }
      return { success: false, message: errorMessage };
    }
  },
  autoLogin: () => {
    const token = localStorage.getItem('token');
    if (token) {
      if (checkTokenExpired()) {
        localStorage.removeItem('token');
        set({ isTokenExpired: true });
      }
      const base64Url = token.split('.')[1]; // JWT의 페이로드 부분
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/'); // URL-safe base64 변환
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      );
      const parsedJsonPayload = JSON.parse(jsonPayload);
      const { name, role } = JSON.parse(parsedJsonPayload.sub);

      set({ isAuthenticated: true, name, role });
    }
  },
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastPath');
    set({ ...initialState });
  },
  tokenExpire: () => {
    localStorage.removeItem('token');
    set({ isTokenExpired: true });
  },
}));
